// T-242c — « Rédiger avec Matthieu » sur le DAL desktop.
// Matthieu LIT (saisie, journal, photos, devis) et PROPOSE un texte par section, avec ses sources ; l'humain relit, corrige ou refuse,
// puis VALIDE section par section ; OmniHub EXÉCUTE (écrit le texte validé). Le PDF n'imprime que les textes validés.
// Aucun montant n'est rédigé par l'IA. Le contrôle AG-02 est affiché ; il bloque la validation tant que le DAL est incomplet.
import { useCallback, useEffect, useState } from "react";
import { Loader2, Sparkles, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { corrigerAction, etatDal, redigerDal, refuserAction, validerAction } from "../../omnihub/communication/matthieuApi";

export function libelleSource(src) {
  const s = String(src || "");
  if (s === "saisie:beneficiaire") return "Saisie : bénéficiaire";
  if (s === "saisie:sante") return "Saisie : santé et autonomie";
  if (s === "saisie:logement") return "Saisie : logement";
  if (s.startsWith("saisie:zone:")) return `Saisie : zone ${s.slice(12).replace(/_/g, " ")}`;
  if (s.startsWith("journal:")) return "Journal de visite";
  if (s.startsWith("photo:")) return `Photo ${Number(s.slice(6)) + 1 || ""}`.trim();
  if (s.startsWith("devis:")) return "Devis";
  return s;
}

const carte = { background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)", borderRadius: 12, padding: 16 };
const secondaire = { background: "transparent", color: "var(--oh-accent-text)", border: "0.5px solid var(--oh-accent)", borderRadius: 8, minHeight: 36,
                     padding: "0 16px", fontWeight: 500 };
const discret = { background: "transparent", color: "var(--oh-text-secondary)", border: "0.5px solid var(--oh-border)", borderRadius: 8, minHeight: 36,
                  padding: "0 16px", fontWeight: 500 };
const champ = { background: "var(--oh-bg-surface)", color: "var(--oh-text)", border: "0.5px solid var(--oh-border)", borderRadius: 8, padding: 12, width: "100%",
                lineHeight: 1.6, fontWeight: 400 };
const pastille = (fam) => (fam === "red"
  ? { background: "var(--oh-error-bg)", color: "var(--oh-error-text)", border: "0.5px solid var(--oh-error-border)", borderRadius: 8 }   // paire charte : contraste AA dans les 2 modes
  : { background: `var(--oh-stat-${fam}-bg)`, color: `var(--oh-stat-${fam}-text)`, borderRadius: 8 });

const messageErreur = (e) => (typeof e?.detail === "string" ? e.detail : e?.message || "Erreur");

function Controle({ controle }) {
  if (!controle) return null;
  if (controle.indisponible) {
    return (
      <div role="alert" className="flex gap-2 text-xs px-3 py-2 mb-3" style={pastille("red")} data-testid="matthieu-dal-ag02">
        <AlertCircle className="w-4 h-4 shrink-0" aria-hidden="true" /> <span>Contrôle AG-02 indisponible : {controle.detail}. La validation reste fermée.</span>
      </div>
    );
  }
  if (controle.bloquant) {
    const manques = controle.manquants_critiques || [];
    const erreurs = (controle.incoherences || []).filter(i => i.severity === "error");
    return (
      <div role="alert" className="text-xs px-3 py-2 mb-3" style={pastille("red")} data-testid="matthieu-dal-ag02">
        <div className="flex gap-2"><AlertCircle className="w-4 h-4 shrink-0" aria-hidden="true" />
          <span style={{ fontWeight: 500 }}>Contrôle AG-02 : le DAL doit être complété avant toute validation.</span></div>
        {manques.length > 0 && <div className="mt-1">Champs critiques manquants : {manques.join(", ")}.</div>}
        {erreurs.map((i, k) => <div key={k} className="mt-1">Incohérence : {i.message}</div>)}
      </div>
    );
  }
  return (
    <div role="status" className="flex gap-2 text-xs px-3 py-2 mb-3" style={pastille("green")} data-testid="matthieu-dal-ag02">
      <CheckCircle2 className="w-4 h-4 shrink-0" aria-hidden="true" /> <span>Contrôle AG-02 : aucun manque critique, aucune incohérence en erreur.</span>
    </div>
  );
}

function Section({ s, occupe, onValider, onCorriger, onRefuser }) {
  const p = s.proposition;
  const [texte, setTexte] = useState(p?.texte || "");
  const [refus, setRefus] = useState(false);
  const [motif, setMotif] = useState("");
  useEffect(() => { setTexte(p?.texte || ""); setRefus(false); setMotif(""); }, [p?.action_id, p?.version, p?.texte]);
  const modifie = !!p && texte.trim() !== (p.texte || "").trim();
  return (
    <article style={{ ...carte, padding: 16 }} data-testid={`matthieu-dal-section-${s.section}`} aria-label={s.titre}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <h4 style={{ color: "var(--oh-text)", fontWeight: 500 }}>{s.titre}</h4>
        {s.valide?.valide_par && (
          <span className="text-xs px-2 py-1" style={pastille("green")} data-testid={`matthieu-dal-valide-${s.section}`}>Validée</span>
        )}
      </div>

      {s.valide?.texte && (
        <div className="mb-3">
          <div className="text-xs mb-1" style={{ color: "var(--oh-text-muted)" }}>Texte validé (celui du PDF)</div>
          <p className="text-sm" style={{ color: "var(--oh-text)", whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{s.valide.texte}</p>
        </div>
      )}

      {p ? (
        <div data-testid={`matthieu-dal-proposition-${s.section}`}>
          <div className="text-xs mb-1" style={{ color: "var(--oh-text-muted)" }}>
            {s.valide?.texte ? "Nouvelle proposition de Matthieu" : "Proposition de Matthieu"}{p.etat === "corrigee" ? " (corrigée)" : ""}
          </div>
          <label htmlFor={`matthieu-dal-texte-${s.section}`} className="sr-only">Texte proposé — {s.titre}</label>
          <textarea id={`matthieu-dal-texte-${s.section}`} value={texte} onChange={e => setTexte(e.target.value)} rows={5} style={champ}
            data-testid={`matthieu-dal-texte-${s.section}`} />
          {(p.sources || []).length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2" data-testid={`matthieu-dal-sources-${s.section}`}>
              <span className="text-xs" style={{ color: "var(--oh-text-muted)" }}>Sources :</span>
              {[...new Set((p.sources || []).map(libelleSource))].map(l => (
                <span key={l} className="text-xs px-2 py-1" style={pastille("blue")}>{l}</span>
              ))}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {modifie ? (
              <button type="button" style={secondaire} disabled={occupe} onClick={() => onCorriger(p, texte.trim())}
                data-testid={`matthieu-dal-corriger-${s.section}`}>Enregistrer la correction</button>
            ) : (
              <button type="button" style={secondaire} disabled={occupe} onClick={() => onValider(p)}
                data-testid={`matthieu-dal-valider-${s.section}`}>Valider cette section</button>
            )}
            <button type="button" style={discret} disabled={occupe} onClick={() => setRefus(v => !v)}
              data-testid={`matthieu-dal-refuser-${s.section}`}>Refuser</button>
          </div>
          {refus && (
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <label htmlFor={`matthieu-dal-motif-${s.section}`} className="sr-only">Motif du refus</label>
              <input id={`matthieu-dal-motif-${s.section}`} value={motif} onChange={e => setMotif(e.target.value)} placeholder="Motif du refus (3 caractères minimum)"
                style={{ ...champ, width: "auto", flex: 1, minHeight: 36, padding: "0 12px" }} data-testid={`matthieu-dal-motif-${s.section}`} />
              <button type="button" style={discret} disabled={occupe || motif.trim().length < 3} onClick={() => onRefuser(p, motif.trim())}
                data-testid={`matthieu-dal-confirmer-refus-${s.section}`}>Confirmer le refus</button>
            </div>
          )}
        </div>
      ) : (
        !s.valide?.texte && (
          <p className="text-xs" style={{ color: "var(--oh-text-secondary)" }} data-testid={`matthieu-dal-vide-${s.section}`}>
            Aucune proposition pour cette section.
          </p>
        )
      )}
    </article>
  );
}

export default function RedigerAvecMatthieuPanel({ dossierId, onValide }) {
  const [etat, setEtat] = useState(null);
  const [statut, setStatut] = useState("chargement");     // chargement | pret | erreur | cache
  const [erreur, setErreur] = useState("");
  const [rediger, setRediger] = useState(false);
  const [occupe, setOccupe] = useState(false);
  const [aCompleter, setACompleter] = useState([]);

  const charger = useCallback(async () => {
    try {
      setEtat(await etatDal(dossierId));
      setStatut("pret");
    } catch (e) {
      if (e?.status === 401 || e?.status === 403) setStatut("cache");      // réservé aux administrateurs : pas de bouton mort
      else { setErreur(messageErreur(e)); setStatut("erreur"); }
    }
  }, [dossierId]);

  useEffect(() => { charger(); }, [charger]);

  const lancer = async () => {
    setRediger(true); setErreur("");
    try {
      const r = await redigerDal(dossierId);
      setACompleter(r.a_completer || []);
      toast.success(r.propositions?.length ? `${r.propositions.length} section(s) proposée(s) — à relire` : "Aucune section proposée");
      await charger();
    } catch (e) {
      const msg = messageErreur(e);
      setErreur(msg); toast.error(msg);
    } finally { setRediger(false); }
  };

  const agir = async (fn, ok) => {
    setOccupe(true); setErreur("");
    try { await fn(); toast.success(ok); await charger(); }
    catch (e) { const msg = messageErreur(e); setErreur(msg); toast.error(msg); await charger(); }
    finally { setOccupe(false); }
  };

  if (statut === "cache") return null;
  return (
    <section style={carte} data-testid="matthieu-dal-panneau" aria-label="Rédiger avec Matthieu">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <h3 className="flex items-center gap-2" style={{ color: "var(--oh-text)", fontWeight: 500 }}>
          <Sparkles className="w-4 h-4" aria-hidden="true" /> Rédiger avec Matthieu
        </h3>
        <button type="button" style={{ ...secondaire, opacity: rediger ? 0.6 : 1 }} disabled={rediger || statut === "chargement"} onClick={lancer}
          data-testid="matthieu-dal-rediger">
          {rediger ? <Loader2 className="w-4 h-4 animate-spin inline mr-1" aria-hidden="true" /> : null}Rédiger avec Matthieu
        </button>
      </div>
      <p className="text-xs mb-3" style={{ color: "var(--oh-text-secondary)" }}>
        Matthieu lit la saisie, le journal de visite, les photos et le devis, puis propose un texte par section avec ses sources. Vous relisez et validez
        chaque section : seul le texte validé figure dans le PDF. Aucun montant n'est rédigé.
      </p>

      {statut === "chargement" && (
        <div className="flex items-center gap-2 text-xs" style={{ color: "var(--oh-text-secondary)" }} data-testid="matthieu-dal-chargement">
          <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" /> Chargement…
        </div>
      )}
      {erreur && (
        <div role="alert" className="text-xs px-3 py-2 mb-3" style={pastille("red")} data-testid="matthieu-dal-erreur">{erreur}</div>
      )}
      {statut === "pret" && etat && (
        <>
          <Controle controle={etat.controle} />
          {aCompleter.length > 0 && (
            <div role="status" className="text-xs px-3 py-2 mb-3" style={pastille("amber")} data-testid="matthieu-dal-a-completer">
              <div style={{ fontWeight: 500 }}>À compléter par vous :</div>
              {aCompleter.map(x => <div key={x.section}>{x.titre} — {x.motif}</div>)}
            </div>
          )}
          {!etat.sections.some(s => s.proposition || s.valide) && (
            <p className="text-xs mb-3" style={{ color: "var(--oh-text-secondary)" }} data-testid="matthieu-dal-etat-vide">
              Rien n'est encore rédigé. {etat.journal_nb ? `Le journal contient ${etat.journal_nb} entrée(s).` : "Ajoutez des notes au journal de visite pour de meilleures propositions."}
            </p>
          )}
          <div className="space-y-3">
            {etat.sections.map(s => (
              <Section key={s.section} s={s} occupe={occupe}
                onValider={p => agir(async () => { await validerAction(p.echange_id, p.action_id, p.version); onValide?.(); }, "Section validée")}
                onCorriger={(p, t) => agir(() => corrigerAction(p.echange_id, p.action_id, p.version, { texte: t }), "Correction enregistrée")}
                onRefuser={(p, m) => agir(() => refuserAction(p.echange_id, p.action_id, p.version, m), "Proposition refusée")} />
            ))}
          </div>
        </>
      )}
    </section>
  );
}
